import { useEffect, useRef } from "react";
import { useRegion } from "@/context/RegionContext";

interface DeliveryTermsProps {
  terms?: string | null;
  accepted: boolean;
  onAcceptedChange: (accepted: boolean) => void;
}

const URL_PATTERN = /https?:\/\/[^\s<]+/g;
const TRAILING_PUNCTUATION = /[.,;:!?'")\]]+$/;

/**
 * Turns bare http(s) URLs in text nodes into clickable links, so a plain URL
 * typed into the admin's rich-text terms (not inserted via its link button)
 * still ends up clickable. Skips text already inside an <a>, so it never
 * double-wraps a link the admin made properly.
 */
function linkifyPlainUrls(container: HTMLElement) {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode: (node) =>
      node.parentElement?.closest("a")
        ? NodeFilter.FILTER_SKIP
        : NodeFilter.FILTER_ACCEPT,
  });

  const textNodes: Text[] = [];
  let node: Node | null;
  while ((node = walker.nextNode())) {
    textNodes.push(node as Text);
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent ?? "";
    URL_PATTERN.lastIndex = 0;
    if (!URL_PATTERN.test(text)) {
      continue;
    }
    URL_PATTERN.lastIndex = 0;

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = URL_PATTERN.exec(text))) {
      if (match.index > lastIndex) {
        fragment.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      let url = match[0];
      let trailing = "";
      const trailingMatch = url.match(TRAILING_PUNCTUATION);
      if (trailingMatch) {
        trailing = trailingMatch[0];
        url = url.slice(0, -trailing.length);
      }

      const link = document.createElement("a");
      link.href = url;
      link.textContent = url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      fragment.appendChild(link);
      if (trailing) {
        fragment.appendChild(document.createTextNode(trailing));
      }

      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }
}

/**
 * Shows the delivery terms (when the admin set any) and the acceptance
 * checkbox + link to the full T&C page. The checkbox/link/button row always
 * renders, even with no admin text, so the booking flow is never blocked by
 * a blank terms field. Controlled by the parent so acceptance state lives
 * with the overall selection.
 */
export default function DeliveryTerms({ terms, accepted, onAcceptedChange }: DeliveryTermsProps) {
  const { region } = useRegion();
  const termsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (termsRef.current) {
      linkifyPlainUrls(termsRef.current);
    }
  }, [terms]);

  return (
    <div className="mt-3 text-xs text-neutral-600">
      {terms && (
        <div
          ref={termsRef}
          className="prose prose-sm mb-2 max-w-none whitespace-pre-line text-neutral-600 [&_a]:font-semibold [&_a]:text-green-600 [&_p]:my-1"
          dangerouslySetInnerHTML={{ __html: terms }}
        />
      )}
      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={accepted}
          onChange={(event) => onAcceptedChange(event.target.checked)}
        />
        <span>
          I have read and agree to the{" "}
          <a
            href={`${region.productionUrl}/pages/car-delivery-collection-terms-and-conditions`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold text-green-600"
            onClick={(e) => e.stopPropagation()}
          >
            delivery terms and conditions
          </a>
          .
        </span>
      </label>
    </div>
  );
}
